<template>
  <div class="flex justify-center items-center h-screen flex-col">
    <VPLoadingSpinner
      :loading="loading"
      :top-message="$gettext('Your order is being processed...')"
      :bottom-message="$gettext('Please, don\'t refresh or close this page, or navigate away')"
    >
      <div class="text-xl text-vp-pink">
        {{ message }}
      </div>
      <VPButton
        class="my-8"
        variant="solid"
        size="lg"
        @click="goBack"
      >
        <iconify-icon
          icon="mdi:undo"
          class="text-4xl mr-2"
        />
        {{ $gettext('Back') }}
      </VPButton>
    </VPLoadingSpinner>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useCheckoutStore } from '@/stores/checkout';
import { useGettext } from 'vue3-gettext';
import { useRecaptcha } from '@/components/recaptcha/useRecaptcha';
import { VPLoadingSpinner } from '@vp/vue-components';
import { until } from '@vueuse/core';
import VPButton from '@/components/form/VPButton.vue';
import { navigate } from 'vike/client/router';
import { OrderDetails, useOrderDetails } from '@/checkout/useOrderDetails';
import * as Sentry from '@sentry/vue';
import { useTracking } from '@/components/tracking/useTracking';
import { HARD_CODED_USD_CHANGE_ME } from '@/helpers/currency';
import { useHive } from '@/checkout/integrations/useHive';
import { useLaylo } from '@/checkout/integrations/useLaylo';

type Props = {
  paymentIntentId?: string;
  orderDetails?: OrderDetails;
};

const props = defineProps<Props>();

const { $gettext } = useGettext();
const { trackEvent } = useTracking();

const processing = ref(true);
const message = ref('');
const router = useRouter();
const checkoutStore = useCheckoutStore();
const recaptcha = useRecaptcha({
  autoInit: true,
  initWatcher: () => checkoutStore.recaptchaEnabled,
});

const { clear: clearOrderDetails } = useOrderDetails();

const loading = computed(() => processing.value || !checkoutStore.initialized);
const ready = computed(() => checkoutStore.initialized);

const goBack = (): void => {
  navigate(router.resolve({
    name: 'OrderPayment',
    params: { eventId: props?.orderDetails?.eventId },
    query: { shoppingCartToken: checkoutStore.shoppingCartToken },
  }).href,
  {
    overwriteLastHistoryEntry: true,
  });
};

onMounted(async () => {
  await until(ready).toBe(true);
  const hive = useHive(checkoutStore.getSelectedEvent.config.hiveBrandId);
  const laylo = useLaylo(checkoutStore.getSelectedEvent.config.layloKey);

  if (!props.orderDetails) {
    message.value = $gettext('No order data found.');
    processing.value = false;
    return;
  }

  try {
    const orderDetails = {
      ...props.orderDetails,
      customerInformation: {
        ...props.orderDetails.customerInformation,
        paymentIntentId: props.paymentIntentId,
      },
    };
    const result = await checkoutStore.completeOrder(orderDetails, recaptcha);
    if (result.checkout.completeOrder.success) {
      message.value = $gettext('Order completed successfully!');
      trackEvent('purchase', {
        currency: HARD_CODED_USD_CHANGE_ME,
        value: result.checkout.completeOrder.orderSummary?.breakdown?.orderTotal,
        transaction_id: result.checkout.completeOrder.orderSummary.fingerprint,
      });

      const nextRoute = router.resolve({
        name: 'OrderConfirmation',
        params: {
          fingerprint: result.checkout.completeOrder.orderSummary.fingerprint,
        },
      });

      try {
        hive.confirmOrder(
          checkoutStore.orderSummary,
          window.location.href.substring(0, window.location.href.lastIndexOf('/')),
        );
      } catch (e) {
        Sentry.captureException(e);
      }

      try {
        laylo.subscribe({
          email: props.orderDetails.customerInformation.customer?.email,
          phoneNumber: props.orderDetails.customerInformation.customer?.phoneNumber,
          optIn: (props.orderDetails.customerInformation.agreements.find(({ key }: { key: string }) => key == 'newsletter') || {})['value'],
          phoneOptIn: (props.orderDetails.customerInformation.agreements.find(({ key }: { key: string }) => key == 'sms_newsletter') || {})['value'],
        });
      } catch (e) {
        Sentry.captureException(e);
      }

      checkoutStore.reset();
      clearOrderDetails();

      const { changeToken } = result.checkout.completeOrder;

      navigate(nextRoute.href, { overwriteLastHistoryEntry: true, pageContext: { changeToken } });
    } else {
      message.value = result.checkout.completeOrder.errors.join(', ');
      processing.value = false;
    }
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, { extra: { paymentIntentId: props.paymentIntentId } });
    message.value = $gettext("Uh oh, your order wasn't successful. You weren't charged, and if you see any pending charges, they'll disappear. If you don't get a confirmation email within 15 minutes, the order didn't go through. Please give it another try, and reach out if you need anything!");
    processing.value = false;
  }
});
</script>
